import { useEffect, useRef, useState } from 'react';
import type { TheaterSession, TheaterState } from '@/theater/TheaterSession';
import { normalizedToHu } from '@/volume/tf';
import { Slider } from '@/theater/TheaterPanel';
import { PANE_COLOR, PANE_LABEL, type PaneId } from './MprSet';
import { Icon } from '@/ui/Icon';

/** Docked "Multiplanar Reformatting" panel: the three panes (X red, Y green, Z blue) and the
 * shared slice settings. */
export function MprPanel({ session, state, onFullscreen, onQuad }: { session: TheaterSession; state: TheaterState; onFullscreen: (p: PaneId) => void; onQuad: () => void }) {
  const [settings, setSettings] = useState(false);
  const w = state.mprWindow;
  const huMin = Math.round(normalizedToHu(w[0]));
  const huMax = Math.round(normalizedToHu(w[1]));
  return (
    <div className="mpr-panel">
      <div className="row mpr-head">
        <h3>Multiplanar Reformatting</h3>
        <span className="spacer" />
        <button className="icon" title="Toggle the 2 × 2 quad view" onClick={onQuad}>
          <Icon name="layout-grid" />
        </button>
        <button className="icon" title="Slice settings" onClick={() => setSettings(!settings)}>
          <Icon name={settings ? 'chevron-up' : 'chevron-down'} />
        </button>
      </div>
      {settings && (
        <div className="mpr-settings">
          <Slider label={`Zoom ${state.mprZoom.toFixed(2)}`} value={state.mprZoom} min={0} max={1} step={0.01} onChange={(v) => session.setMprZoom(v)} />
          <div className="muted small">
            W {huMax - huMin} / L {Math.round((huMin + huMax) / 2)} HU · {huMin} to {huMax} HU
          </div>
          <Slider label="Window min" value={w[0]} min={0} max={1} step={0.001} onChange={(v) => session.setMprWindow(Math.min(v, w[1] - 0.001), w[1])} />
          <Slider label="Window max" value={w[1]} min={0} max={1} step={0.001} onChange={(v) => session.setMprWindow(w[0], Math.max(v, w[0] + 0.001))} />
          <Slider label={`Value gain ${state.mprGain.toFixed(2)}`} value={state.mprGain} min={-1} max={1} step={0.01} onChange={(v) => session.setMprGain(v)} />
          <Slider label={`Contrast ${state.mprContrast.toFixed(2)}`} value={state.mprContrast} min={0} max={2} step={0.01} onChange={(v) => session.setMprContrast(v)} />
          <label className="check">
            <input type="checkbox" checked={state.mprOverlays} onChange={(e) => session.setMprOverlays(e.target.checked)} /> Measurement overlays
          </label>
          <label className="check">
            <input type="checkbox" checked={state.mprLabels} onChange={(e) => session.setMprLabels(e.target.checked)} /> Cusp label overlay
          </label>
          <label className="check" title="Paint the leaflet fill of a Clean Root / Leaflets preset red on the slices">
            <input type="checkbox" checked={state.mprLeaflets} disabled={!state.leafletOverlay} onChange={(e) => session.setMprLeaflets(e.target.checked)} /> Leaflet overlay
          </label>
          <button className="secondary small" onClick={() => session.mpr?.resetOffsets()}>
            Reset slices
          </button>
        </div>
      )}
      {(['y', 'x', 'z'] as PaneId[]).map((p) => (
        <MprPane key={p} session={session} pane={p} className="docked" onFullscreen={() => onFullscreen(p)} />
      ))}
    </div>
  );
}

type Drag =
  | { kind: 'scrub'; startY: number; startOffset: number }
  | { kind: 'pan'; lastX: number; lastY: number }
  | { kind: 'zoom'; startY: number; startZoom: number; moved: boolean; startX: number }
  | { kind: 'wl'; startX: number; startY: number; startWindow: [number, number] }
  | { kind: 'crosshair'; axes: PaneId[] }
  | { kind: 'rotate'; lastAngle: number }
  | { kind: 'landmark'; key: string; depth: number; moved: boolean };

export function MprPane({ session, pane, className, onFullscreen, fullscreen }: { session: TheaterSession; pane: PaneId; className?: string; onFullscreen: () => void; fullscreen?: boolean }) {
  const elRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const [hover, setHover] = useState<'move' | 'rotate' | null>(null);
  const label = PANE_LABEL[pane];
  const color = PANE_COLOR[pane];
  const offset = session.state.mprOffsets[pane];
  const [, setTick] = useState(0);

  useEffect(() => {
    const el = elRef.current;
    const canvas = canvasRef.current;
    if (!el || !canvas || !session.mpr) return;
    session.mpr.bind(pane, el, canvas);
    const unsub = session.subscribe(() => setTick((t) => t + 1));
    return () => {
      unsub();
      session.mpr?.unbind(pane);
    };
  }, [session, pane, session.mpr]);

  const local = (e: { clientX: number; clientY: number }) => {
    const r = elRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, w: r.width, h: r.height };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const mpr = session.mpr;
    if (!mpr) return;
    e.preventDefault();
    elRef.current!.setPointerCapture(e.pointerId);
    const p = local(e);
    if ((e.buttons & 1) !== 0 && (e.buttons & 2) !== 0) {
      dragRef.current = { kind: 'wl', startX: e.clientX, startY: e.clientY, startWindow: mpr.window };
      return;
    }
    if (e.button === 0) {
      // a landmark dot in edit mode drags on the slice plane; its depth along the normal stays
      const landmark = session.landmarkAtPanePixel(pane, p.x, p.y, true);
      if (landmark) {
        const world = session.sceneToWorld(session.corrections!.position(landmark)!);
        const depth = world.sub(mpr.sliceCentre(pane)).dot(mpr.panes[pane].normal);
        dragRef.current = { kind: 'landmark', key: landmark, depth, moved: false };
        return;
      }
      const hit = mpr.hitCrosshair(pane, p.x, p.y);
      if (hit?.mode === 'both' || hit?.mode === 'drag') dragRef.current = { kind: 'crosshair', axes: Array.isArray(hit.axis) ? hit.axis : [hit.axis] };
      else if (hit?.mode === 'rotate') {
        const c = mpr.crosshairCenterPx(pane)!;
        dragRef.current = { kind: 'rotate', lastAngle: (Math.atan2(p.y - c.y, p.x - c.x) * 180) / Math.PI };
      } else dragRef.current = { kind: 'scrub', startY: e.clientY, startOffset: mpr.panes[pane].offset };
    } else if (e.button === 2) dragRef.current = { kind: 'pan', lastX: e.clientX, lastY: e.clientY };
    else if (e.button === 1) dragRef.current = { kind: 'zoom', startY: e.clientY, startX: e.clientX, startZoom: mpr.panes[pane].zoom, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const mpr = session.mpr;
    if (!mpr) return;
    const p = local(e);
    const d = dragRef.current;
    if (!d) {
      if (session.landmarkAtPanePixel(pane, p.x, p.y, true)) {
        setHover('move');
        return;
      }
      const hit = mpr.hitCrosshair(pane, p.x, p.y);
      setHover(hit ? (hit.mode === 'rotate' ? 'rotate' : 'move') : null);
      return;
    }
    if ((e.buttons & 1) !== 0 && (e.buttons & 2) !== 0 && d.kind !== 'wl') {
      dragRef.current = { kind: 'wl', startX: e.clientX, startY: e.clientY, startWindow: mpr.window };
      return;
    }
    switch (d.kind) {
      case 'scrub': {
        const v = d.startOffset - (e.clientY - d.startY) * 0.1;
        session.setMprOffset(pane, fullscreen ? Math.max(-200, Math.min(200, v)) : v);
        break;
      }
      case 'pan': {
        session.mpr?.panByViewportDelta(pane, (e.clientX - d.lastX) / p.w, -(e.clientY - d.lastY) / p.h);
        d.lastX = e.clientX;
        d.lastY = e.clientY;
        break;
      }
      case 'zoom': {
        if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 5) d.moved = true;
        session.setMprZoom(d.startZoom - (e.clientY - d.startY) * 0.002, pane);
        break;
      }
      case 'wl':
        session.mprWindowLevelDrag(d.startWindow, e.clientX - d.startX, e.clientY - d.startY);
        break;
      case 'crosshair': {
        const world = mpr.pixelToWorld(pane, p.x, p.y);
        if (world) {
          mpr.moveSlicesTo(d.axes, world);
          session.syncMprOffsets();
        }
        break;
      }
      case 'landmark': {
        const world = mpr.pixelToWorld(pane, p.x, p.y);
        if (!world) break;
        world.add(mpr.panes[pane].normal.clone().multiplyScalar(d.depth));
        d.moved = true;
        session.moveLandmark(d.key, session.worldToScene(world), true);
        break;
      }
      case 'rotate': {
        const c = mpr.crosshairCenterPx(pane);
        if (!c) break;
        const a = (Math.atan2(p.y - c.y, p.x - c.x) * 180) / Math.PI;
        let delta = a - d.lastAngle;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;
        d.lastAngle = a;
        mpr.rotateTriad(pane, delta);
        session.syncMprOffsets();
        break;
      }
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = dragRef.current;
    const mpr = session.mpr;
    if (d?.kind === 'landmark' && d.moved) {
      const pos = session.corrections?.position(d.key);
      if (pos) session.moveLandmark(d.key, pos, false);
    }
    if (d?.kind === 'zoom' && !d.moved && mpr) {
      const p = local(e);
      const world = mpr.pixelToWorld(pane, p.x, p.y);
      if (world) {
        const others = (['x', 'y', 'z'] as PaneId[]).filter((id) => id !== pane);
        mpr.moveSlicesTo(others, world);
        session.syncMprOffsets();
      }
    }
    if (e.buttons === 0) {
      dragRef.current = null;
      if (elRef.current?.hasPointerCapture(e.pointerId)) elRef.current.releasePointerCapture(e.pointerId);
    }
  };

  // Native listener: React registers `onWheel` as passive, so preventDefault there cannot stop
  // the panel from scrolling while the wheel steps the slice.
  const wheelRef = useRef<(e: WheelEvent) => void>(() => {});
  wheelRef.current = (e: WheelEvent) => {
    e.preventDefault();
    const delta = Math.max(-5, Math.min(5, (-e.deltaY / 100) * 0.5));
    const v = (session.mpr?.panes[pane].offset ?? 0) + delta;
    session.setMprOffset(pane, fullscreen ? Math.max(-200, Math.min(200, v)) : v);
  };
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => wheelRef.current(e);
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <div className={`mpr-pane ${className ?? ''}`} style={{ borderColor: color }}>
      <div className="mpr-pane-head" style={{ color }}>
        <span className="pane-label">{label}</span>
        <span className="spacer" />
        <button className={`icon ${session.state.mprPlanesIn3D[pane] ? 'on' : ''}`} title={`Show / hide the ${label} plane in the 3D view`} onClick={() => session.setMprPlaneIn3D(pane, !session.state.mprPlanesIn3D[pane])}>
          <Icon name="eye" />
        </button>
        <button className="icon" title={fullscreen ? 'Exit fullscreen' : `Fullscreen ${label} view`} onClick={onFullscreen}>
          <Icon name={fullscreen ? 'minimize-2' : 'maximize-2'} />
        </button>
      </div>
      <div
        className={`mpr-view ${hover === 'move' ? 'cursor-move' : hover === 'rotate' ? 'cursor-rotate' : ''}`}
        ref={elRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onDoubleClick={(e) => {
          const p = local(e);
          const lm = session.landmarkAtPanePixel(pane, p.x, p.y, false);
          if (lm) session.setLandmarkEditing(lm, true);
          else session.clearLandmarkEditing();
        }}
        onPointerCancel={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
      >
        <canvas ref={canvasRef} className="mpr-overlay" />
      </div>
      <div className="mpr-strip">
        <span className="small" style={{ color }}>
          {label} Slice
        </span>
        <input type="range" min={-200} max={200} step={0.5} value={Math.max(-200, Math.min(200, offset))} onChange={(e) => session.setMprOffset(pane, Number(e.target.value))} />
        <span className="small mono">{offset.toFixed(1)}</span>
        {fullscreen && (
          <>
            <span className="small">Zoom</span>
            <input type="range" min={0.1} max={1} step={0.01} value={session.mpr?.panes[pane].zoom ?? 0.5} onChange={(e) => session.setMprZoom(Number(e.target.value), pane)} />
          </>
        )}
      </div>
    </div>
  );
}
